// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container } from 'reactstrap';
import AppointmentInfo from 'components/molecules/AppointmentInfo';
import ContentFooter from 'components/pages/components/ContentFooter';
import Alert from 'components/atoms/Alert';
import Panel from 'components/atoms/Panel';
import Button from 'components/atoms/Button';
import ParseHTML from 'components/atoms/ParseHTML';
import VehiclePanel from 'components/atoms/VehiclePanelWithGrayOut';

// Constants
import {
  SCI_VEHICLE_ROUTE,
  SELF_CHECK_IN_FLOW,
  SCI_ERROR_ROUTE
} from 'constants/selfCheckIn';
import { SCI_FOOTER_KEYS } from 'constants/localizationKeys';

import './LandingPage.scss';

const ns = 'landing-page';
const {
  NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN,
  NON_OPERATING_HOURS_CHECK_IN
} = SELF_CHECK_IN_FLOW;

export type Props = {
  intl: IntlShape,
  vehicle: Object,
  dealerCountryCode: string,
  startTime: Object,
  goTo: (path: string) => void,
  selfCheckInFlow: string,
  gtmStartCheckInClick: () => void,
  selfCheckInError: ?string,
  initialLoadingComplete: boolean
};

const LandingPage = ({
  goTo,
  intl,
  vehicle,
  dealerCountryCode,
  startTime,
  selfCheckInFlow,
  gtmStartCheckInClick,
  selfCheckInError,
  initialLoadingComplete
}: Props) => {
  if (initialLoadingComplete && selfCheckInError) {
    goTo(SCI_ERROR_ROUTE);
  }

  const canStart = Boolean(vehicle.make);
  let warningInfo = intl.formatMessage({
    id: 'c8.self.check.in.landing.page.check.in.should.only.be.completed',
    defaultMessage:
      'Check in should only be completed once you have arrived at the dealership'
  });
  let instructions = intl.formatMessage({
    id: 'c8.self.check.in.config.initial.instructions',
    defaultMessage:
      '<ul>' +
      '<li>Parking is open except for the Service Drive Lane</li>' +
      '<li>Keys can be dropped off with any advisor or in the service department drop box</li>' +
      '<li>To speak with a service advisor, complete checking in and indicate your preferred method of contact</li>' +
      '</ul>'
  });

  if (
    selfCheckInFlow === NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN ||
    selfCheckInFlow === NON_OPERATING_HOURS_CHECK_IN
  ) {
    instructions = intl.formatMessage({
      id: 'c8.self.check.in.config.extended.initial.instructions',
      defaultMessage:
        '<ul>' +
        '<li>Complete the check in process here</li>' +
        '<li>Please park your vehicle in designated location</li>' +
        '<li>Drop the keys in the provided envelope in the drop box</li>' +
        '</ul>'
    });
    warningInfo = intl.formatMessage({
      id: 'c8.self.check.in.landing.page.extended.closed.dealer.warning',
      defaultMessage:
        'The dealership is currently closed, but you can still complete your check in.'
    });
  }
  // TODO: remove default message when localizations are done
  return (
    <div className={ns} role={'main'}>
      <div className={`${ns}__header`}>
        <AppointmentInfo key="appointmentInfo" />
      </div>
      <div>
        <Container className={`${ns}__body`}>
          <h1 className={`${ns}__title mb-3`}>
            {intl.formatMessage({
              id: 'c8.self.check.in.common.final.check.in',
              defaultMessage: 'Check In'
            })}
          </h1>
          <Alert color="warning" icon="error_outline">
            {warningInfo}
          </Alert>
          <VehiclePanel
            vehicleInfo={vehicle}
            dealerCountryCode={dealerCountryCode}
            startTime={startTime}
            addMask={true}
          />
          <div className={`${ns}__sub-title`}>
            {intl.formatMessage({
              id: 'c8.self.check.in.landing.page.instructions'
            })}
          </div>
          <Panel>
            <ParseHTML className={`${ns}__paragraphs`} html={instructions} />
            <Button
              className={`${ns}__button`}
              onClick={() => {
                gtmStartCheckInClick();
                goTo(SCI_VEHICLE_ROUTE);
              }}
              disabled={!canStart}
            >
              {intl.formatMessage({
                id: 'c8.self.check.in.landing.page.final.start.check.in',
                defaultMessage: 'START CHECK IN'
              })}
            </Button>
          </Panel>
        </Container>
        <div className={`${ns}__footer`}>
          <ContentFooter localizationKeys={SCI_FOOTER_KEYS} />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/LandingPage/LandingPage.js